import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {put, select, takeLatest} from 'redux-saga/effects'
import {SSOUserModel, UserModel} from '../models/UserModel'
import {IMenu} from '../models/MenuModel'
import {getMenu, getUserByToken} from './AuthCRUD'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import * as rfSatkerActions from '../../rf-satuan-kerja'
import * as rfSatuanIndikatorActions from '../../rf-satuan-indikator'
import * as tahunAnggaranActions from '../../tahun-anggaran'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  MenuRequested: '[Request Menu] Action',
  MenuLoaded: '[Load Menu] Action',
}

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
  loginLoading: false,
  menu: []
}

export interface IAuthState {
  user?: SSOUserModel
  accessToken?: string
  loginLoading?: boolean
  menu?: IMenu[]
}

export const reducer = persistReducer(
  {storage, key: 'v100-demo1-auth', whitelist: ['user', 'accessToken', 'menu']},
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        const accessToken = action.payload?.accessToken
        return {...state, accessToken, user: undefined, loginLoading: true, menu: undefined}
      }

      case actionTypes.Register: {
        const accessToken = action.payload?.accessToken
        return {...state, accessToken, user: undefined}
      }

      case actionTypes.Logout: {
        return {
          ...state,
          accessToken: initialAuthState.accessToken,
          user: initialAuthState.user,
          loginLoading: initialAuthState.loginLoading,
          menu: initialAuthState.menu
        }
      }

      case actionTypes.UserRequested: {
        const accessToken = action.payload?.accessToken
        return {...state, accessToken, user: undefined}
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user
        return {...state, user}
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user
        return {...state, user}
      }

      case actionTypes.MenuRequested: {
        return {...state, menu: undefined}
      }

      case actionTypes.MenuLoaded: {
        const menu = action.payload?.menu
        return {...state, menu, loginLoading: false}
      }

      default:
        return state
    }
  }
)

export const actions = {
  login: (accessToken: string) => ({type: actionTypes.Login, payload: {accessToken}}),
  register: (accessToken: string) => ({
    type: actionTypes.Register,
    payload: {accessToken},
  }),
  logout: () => ({type: actionTypes.Logout}),
  requestUser: (accessToken: string) => ({
    type: actionTypes.UserRequested,
    payload: {accessToken},
  }),
  fulfillUser: (user: SSOUserModel) => ({type: actionTypes.UserLoaded, payload: {user}}),
  setUser: (user: SSOUserModel) => ({type: actionTypes.SetUser, payload: {user}}),
  getMenu: (accessToken: string) => ({type: actionTypes.MenuRequested, payload: {accessToken}}),
  fulfillMenu: (menu: IMenu[]) => ({type: actionTypes.MenuLoaded, payload: {menu}})
}

export function* saga() {
  yield takeLatest(
    actionTypes.Login,
    function* loginSaga({
      type,
      payload,
    }: {
      type: typeof actionTypes.Login
      payload: {accessToken: string}
    }) {
      yield put(actions.requestUser(payload.accessToken))
    }
  )

  yield takeLatest(
    actionTypes.Register,
    function* registerSaga({type, payload}: {type: typeof actionTypes.Register; payload: string}) {
      yield put(actions.requestUser(payload))
    }
  )

  yield takeLatest(
    actionTypes.UserRequested,
    function* userRequested({
      type,
      payload,
    }: {
      type: typeof actionTypes.UserRequested
      payload: {accessToken: string}
    }) {
      const {data: user} = yield getUserByToken(payload.accessToken)
      yield put(actions.fulfillUser(user))
    }
  )

  yield takeLatest(
    actionTypes.UserLoaded,
    function* menuRequested({
      type,
      payload
    }:{
      type: typeof actionTypes.UserLoaded,
      payload: {accessToken: string}
    }) {
      const {data: {data: menu}} = yield getMenu(payload.accessToken)
      yield put(actions.fulfillMenu(menu))
    }
  )

  yield takeLatest(
    actionTypes.UserLoaded,
    function* rfSatkerRequested({
      type
    }:{
      type: typeof actionTypes.UserLoaded,
    }) {
      yield put(rfSatkerActions.actions.initRFSatuanKerja())
      yield put(rfSatuanIndikatorActions.actions.initRFSatuanIndikator())
      yield put(tahunAnggaranActions.actions.initFiscalYearArray())
    }
  )
}
